import React, {
    useEffect,
    useMemo,
    useState,
} from 'react'

import PulseLoader from 'react-spinners/PulseLoader'

import {
    useGetCoachUserSessionEvalSharesQuery,
    usePublishCoachUserSessionEvalShareMutation,
    useRevokeCoachUserSessionEvalShareMutation,
} from './coachUserSessionEvalSharesApiSlice'


/*
|--------------------------------------------------------------------------
| 01 - Helpers
|--------------------------------------------------------------------------
*/

const getId = value => {

    if (!value) return ''

    if (typeof value === 'string') {
        return value
    }

    return String(
        value._id || value.id || ''
    )

}


const formatScore = value => {

    return typeof value === 'number'
        ? value.toFixed(2).replace(/\.?0+$/, '')
        : '—'

}


const formatDate = value => {

    if (!value) return '—'

    const date = new Date(value)

    return Number.isNaN(date.getTime())
        ? '—'
        : date.toLocaleString()

}


const getErrorMessage = error => {

    return (
        error?.data?.message ||
        error?.data?.error ||
        error?.error ||
        'Unable to complete sharing request.'
    )

}


/*
|--------------------------------------------------------------------------
| 02 - Coach Evaluation Sharing
|--------------------------------------------------------------------------
|
| First Created - 28 Oct 2026
|
| Step 11D - Coach Controlled Sharing
|
| The existing evaluation matrix remains private.
|
| Only selected dimensions and deliberately approved
| feedback are submitted for participant sharing.
|
|--------------------------------------------------------------------------
*/

const EtCoachUserSessionEvalSharing = ({
    evaluationMatrix,
    categories = [],
    dimensions = [],
}) => {

    // First Created - 28 Oct 2026
    // Author - Atul Pandey

    const evalMatrixId = getId(
        evaluationMatrix?._id
    )


    /*
    |--------------------------------------------------------------------------
    | 03 - Sharing UI State
    |--------------------------------------------------------------------------
    */

    const [showSharing, setShowSharing] =
        useState(false)

    const [sharingMode, setSharingMode] =
        useState('ProgressOnly')

    const [selectedDimensionIds, setSelectedDimensionIds] =
        useState([])

    const [sharedFeedback, setSharedFeedback] =
        useState({})

    const [showPreview, setShowPreview] =
        useState(false)

    const [confirmRevoke, setConfirmRevoke] =
        useState(false)

    const [errorMessage, setErrorMessage] =
        useState('')

    const [statusMessage, setStatusMessage] =
        useState('')


    /*
    |--------------------------------------------------------------------------
    | 04 - Sharing Status Query
    |--------------------------------------------------------------------------
    */

    const {
        data: shareData,
        isLoading: isShareLoading,
        isFetching: isShareFetching,
        isError: isShareError,
        refetch: refetchShare,
    } = useGetCoachUserSessionEvalSharesQuery(
        evalMatrixId,
        {
            skip: !evalMatrixId,
        }
    )


    /*
    |--------------------------------------------------------------------------
    | 05 - Sharing Mutations
    |--------------------------------------------------------------------------
    */

    const [
        publishShare,
        {
            isLoading: isPublishing,
        },
    ] = usePublishCoachUserSessionEvalShareMutation()

    const [
        revokeShare,
        {
            isLoading: isRevoking,
        },
    ] = useRevokeCoachUserSessionEvalShareMutation()


    /*
    |--------------------------------------------------------------------------
    | 06 - Available Scored Dimensions
    |--------------------------------------------------------------------------
    |
    | Private evalNote is deliberately not included.
    |
    */

    const availableDimensions = useMemo(() => {

        return (evaluationMatrix?.sessionEvals || [])
            .filter(
                item =>
                    typeof item.score === 'number' &&
                    Number.isInteger(item.score) &&
                    item.score >= 1 &&
                    item.score <= 5
            )
            .map(item => {

                const dimensionId = getId(
                    item.dimensionId
                )

                const definition = dimensions.find(
                    d => getId(d) === dimensionId
                )

                const categoryId = getId(
                    definition?.categoryId
                )

                const category = categories.find(
                    c => getId(c) === categoryId
                )

                return {

                    dimensionId,

                    dimensionName:
                        definition?.dimensionName ||
                        'Evaluation Dimension',

                    categoryId,

                    categoryName:
                        category?.categoryName ||
                        'Uncategorized',

                    score: item.score,

                }

            })
            .filter(item => item.dimensionId)

    }, [
        evaluationMatrix,
        categories,
        dimensions,
    ])


    /*
    |--------------------------------------------------------------------------
    | 07 - Current Approved Publication
    |--------------------------------------------------------------------------
    */

    const currentVersion =
        shareData?.currentVersion || 0

    const isShared =
        shareData?.sharingStatus === 'Shared'

    const latestPublishedVersion = useMemo(() => {

        return shareData?.versions?.find(
            version =>
                version.version === currentVersion
        ) || null

    }, [
        shareData,
        currentVersion,
    ])


    /*
    |--------------------------------------------------------------------------
    | 08 - Initialize The Sharing Draft
    |--------------------------------------------------------------------------
    |
    | A published snapshot can be opened for revision.
    |
    | Opening sharing after a refresh starts with
    | the latest approved selections.
    |
    */

    useEffect(() => {

        if (!showSharing || isShareLoading || isShareFetching) {
            return
        }

        const existingVersion =
            shareData?.versions?.find(
                version =>
                    version.version ===
                    shareData.currentVersion
            )

        if (existingVersion) {

            setSharingMode(
                existingVersion.sharingMode
            )

            setSelectedDimensionIds(
                existingVersion.dimensions.map(
                    item => getId(item.dimensionId)
                )
            )

            const feedback = {}

            existingVersion.dimensions.forEach(
                item => {

                    feedback[getId(item.dimensionId)] =
                        item.sharedFeedback || ''

                }
            )

            setSharedFeedback(feedback)

        } else {

            setSharingMode('ProgressOnly')

            setSelectedDimensionIds(
                availableDimensions.map(
                    item => item.dimensionId
                )
            )

            setSharedFeedback({})

        }

        setShowPreview(false)
        setConfirmRevoke(false)
        setErrorMessage('')
        setStatusMessage('')

    }, [
        showSharing,
        evalMatrixId,
        shareData,
        isShareLoading,
        isShareFetching,
        availableDimensions,
    ])


    /*
    |--------------------------------------------------------------------------
    | 09 - Select Or Deselect Dimension
    |--------------------------------------------------------------------------
    */

    const toggleDimension = dimensionId => {

        setSelectedDimensionIds(previous => {

            return previous.includes(dimensionId)
                ? previous.filter(
                    id => id !== dimensionId
                )
                : [...previous, dimensionId]

        })

        setShowPreview(false)
        setErrorMessage('')

    }


    /*
    |--------------------------------------------------------------------------
    | 10 - Selected Preview Dimensions
    |--------------------------------------------------------------------------
    */

    const previewDimensions = useMemo(() => {

        return availableDimensions
            .filter(
                item =>
                    selectedDimensionIds.includes(
                        item.dimensionId
                    )
            )
            .map(item => ({

                ...item,

                sharedFeedback:
                    sharingMode === 'ProgressAndFeedback'
                        ? (
                            sharedFeedback[
                                item.dimensionId
                            ] || ''
                        )
                        : '',

            }))

    }, [
        availableDimensions,
        selectedDimensionIds,
        sharingMode,
        sharedFeedback,
    ])


    /*
    |--------------------------------------------------------------------------
    | 11 - Publish Approved Snapshot
    |--------------------------------------------------------------------------
    */

    const handlePublish = async () => {

        setErrorMessage('')
        setStatusMessage('')

        if (previewDimensions.length === 0) {

            setErrorMessage(
                'Select at least one dimension to share.'
            )

            return

        }

        if (!showPreview) {

            setErrorMessage(
                'Review the participant preview before publishing.'
            )

            return

        }

        try {

            await publishShare({

                evalMatrixId,

                sharingMode,

                expectedVersion:
                    currentVersion,

                dimensions:
                    previewDimensions.map(item => ({

                        dimensionId:
                            item.dimensionId,

                        sharedFeedback:
                            item.sharedFeedback,

                    })),

            }).unwrap()

            setStatusMessage(
                'Evaluation successfully shared with the participant.'
            )

            setShowPreview(false)
            setShowSharing(false)

            await refetchShare()

        } catch (error) {

            setErrorMessage(
                getErrorMessage(error)
            )

        }

    }


    /*
    |--------------------------------------------------------------------------
    | 12 - Revoke Sharing
    |--------------------------------------------------------------------------
    */

    const handleRevoke = async () => {

        setErrorMessage('')
        setStatusMessage('')

        try {

            await revokeShare({

                evalMatrixId,

                expectedVersion:
                    currentVersion,

            }).unwrap()

            setStatusMessage(
                'Participant access has been revoked.'
            )

            setConfirmRevoke(false)
            setShowPreview(false)
            setShowSharing(false)

            await refetchShare()

        } catch (error) {

            setErrorMessage(
                getErrorMessage(error)
            )

        }

    }


    /*
    |--------------------------------------------------------------------------
    | 13 - Rendering
    |--------------------------------------------------------------------------
    */

    if (!evalMatrixId) {
        return null
    }

    const isBusy =
        isPublishing ||
        isRevoking ||
        isShareFetching

    return (

        <div className="mt-3 rounded-lg border border-violet-200 bg-violet-50/40 p-3 font-poppins">

            {/*----------------------------------------------------------
              Sharing Header
            ----------------------------------------------------------*/}

            <div className="flex flex-wrap items-center justify-between gap-3">

                <div>

                    <p className="text-xs font-bold text-violet-700">
                        Evaluation Sharing
                    </p>

                    <p className="mt-1 text-[10px] text-gray-500">
                        Control which evaluation scores and
                        feedback are visible to the participant.
                    </p>

                </div>

                <div className="flex flex-wrap items-center gap-2">

                    <span
                        className={`rounded-full px-3 py-1 text-[10px] font-bold ${
                            isShared
                                ? 'bg-lime-100 text-lime-700'
                                : shareData?.sharingStatus === 'Revoked'
                                    ? 'bg-rose-100 text-rose-700'
                                    : 'bg-gray-100 text-gray-600'
                        }`}
                    >
                        {isShared
                            ? 'Shared'
                            : shareData?.sharingStatus === 'Revoked'
                                ? 'Revoked'
                                : 'Private'}
                    </span>

                    <button
                        type="button"
                        disabled={
                            isBusy ||
                            isShareLoading ||
                            isShareError
                        }
                        onClick={() => {

                            setShowSharing(previous => !previous)

                            setErrorMessage('')
                            setStatusMessage('')

                        }}
                        className="rounded-full border border-violet-300 bg-white px-3 py-1 text-[10px] font-bold text-violet-700 hover:bg-violet-50 disabled:opacity-50"
                    >
                        {showSharing
                            ? 'Close Sharing'
                            : isShared
                                ? 'Manage Sharing'
                                : 'Prepare Sharing'}
                    </button>

                </div>

            </div>


            {/*----------------------------------------------------------
              Current Sharing Summary
            ----------------------------------------------------------*/}

            {isShareLoading && (

                <div className="mt-3 flex justify-center">

                    <PulseLoader
                        color="#7c3aed"
                        size={5}
                    />

                </div>

            )}

            {isShareError && (

                <p className="mt-2 text-[10px] text-red-600">
                    Unable to retrieve the evaluation sharing status.
                </p>

            )}

            {latestPublishedVersion && !showSharing && (

                <div className="mt-3 rounded border border-violet-100 bg-white/80 p-3">

                    <p className="text-[10px] text-gray-600">
                        Version {currentVersion}
                        {' · '}
                        {latestPublishedVersion.sharingMode ===
                        'ProgressOnly'
                            ? 'Progress Only'
                            : 'Progress + Feedback'}
                        {' · '}
                        {latestPublishedVersion.dimensions.length}
                        {' '}dimensions
                    </p>

                    <p className="mt-1 text-[10px] text-gray-400">
                        Last published:
                        {' '}
                        {formatDate(
                            latestPublishedVersion.publishedAt
                        )}
                    </p>

                </div>

            )}


            {/*----------------------------------------------------------
              Preparation And Publication
            ----------------------------------------------------------*/}

            {showSharing && !isShareLoading && !isShareError && (

                <div className="mt-3 space-y-4 border-t border-violet-100 pt-3">

                    {/* Sharing Mode */}

                    <div>

                        <p className="mb-2 text-[11px] font-bold text-gray-700">
                            1. Choose Sharing Mode
                        </p>

                        <div className="flex flex-wrap gap-2">

                            {[
                                {
                                    id: 'ProgressOnly',
                                    label: 'Progress Only',
                                },
                                {
                                    id: 'ProgressAndFeedback',
                                    label: 'Progress + Feedback',
                                },
                            ].map(mode => (

                                <button
                                    key={mode.id}
                                    type="button"
                                    disabled={isBusy}
                                    onClick={() => {

                                        setSharingMode(mode.id)

                                        setShowPreview(false)

                                    }}
                                    className={`rounded-full border px-4 py-2 text-[10px] font-semibold ${
                                        sharingMode === mode.id
                                            ? 'border-violet-600 bg-violet-600 text-white'
                                            : 'border-gray-200 bg-white text-gray-600'
                                    }`}
                                >
                                    {mode.label}
                                </button>

                            ))}

                        </div>

                        <p className="mt-2 text-[10px] text-gray-500">
                            Private coach evaluation comments
                            are never published automatically.
                        </p>

                    </div>


                    {/* Dimension Selection */}

                    <div>

                        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">

                            <p className="text-[11px] font-bold text-gray-700">
                                2. Select Dimensions
                            </p>

                            <span className="text-[10px] text-violet-700">
                                {selectedDimensionIds.length}
                                {' '}selected
                            </span>

                        </div>

                        <div className="max-h-72 space-y-2 overflow-y-auto">

                            {availableDimensions.map(item => {

                                const selected =
                                    selectedDimensionIds.includes(
                                        item.dimensionId
                                    )

                                return (

                                    <div
                                        key={item.dimensionId}
                                        className={`rounded-lg border p-3 ${
                                            selected
                                                ? 'border-violet-200 bg-white'
                                                : 'border-gray-200 bg-gray-50'
                                        }`}
                                    >

                                        <label className="flex cursor-pointer items-start gap-3">

                                            <input
                                                type="checkbox"
                                                checked={selected}
                                                disabled={isBusy}
                                                onChange={() =>
                                                    toggleDimension(
                                                        item.dimensionId
                                                    )
                                                }
                                                className="mt-1 accent-violet-600"
                                            />

                                            <div className="min-w-0 flex-1">

                                                <p className="text-[9px] font-semibold text-gray-400">
                                                    {item.categoryName}
                                                </p>

                                                <p className="text-[11px] font-bold text-gray-700">
                                                    {item.dimensionName}
                                                </p>

                                            </div>

                                            <span className="rounded-full bg-orange-50 px-3 py-1 text-xs font-bold text-orange-600">
                                                {formatScore(item.score)} / 5
                                            </span>

                                        </label>


                                        {/* Participant Facing Feedback */}

                                        {selected &&
                                            sharingMode ===
                                            'ProgressAndFeedback' && (

                                            <div className="mt-3">

                                                <label
                                                    htmlFor={`sharing-feedback-${evalMatrixId}-${item.dimensionId}`}
                                                    className="mb-1 block text-[10px] font-semibold text-violet-700"
                                                >
                                                    Approved Participant Feedback
                                                </label>

                                                <textarea
                                                    id={`sharing-feedback-${evalMatrixId}-${item.dimensionId}`}
                                                    rows={3}
                                                    maxLength={5000}
                                                    disabled={isBusy}
                                                    value={
                                                        sharedFeedback[
                                                            item.dimensionId
                                                        ] || ''
                                                    }
                                                    onChange={event => {

                                                        const value =
                                                            event.target.value

                                                        setSharedFeedback(
                                                            previous => ({

                                                                ...previous,

                                                                [item.dimensionId]:
                                                                    value,

                                                            })
                                                        )

                                                        setShowPreview(false)

                                                    }}
                                                    placeholder="Write feedback intended specifically for the participant..."
                                                    className="w-full rounded border border-violet-200 bg-white p-2 text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-violet-200"
                                                />

                                            </div>

                                        )}

                                    </div>

                                )

                            })}

                        </div>

                        {availableDimensions.length === 0 && (

                            <p className="text-xs italic text-gray-500">
                                No scored dimensions are available
                                for sharing.
                            </p>

                        )}

                    </div>


                    {/* Preview Control */}

                    <div className="border-t border-violet-100 pt-3">

                        <button
                            type="button"
                            disabled={
                                previewDimensions.length === 0 ||
                                isBusy
                            }
                            onClick={() =>
                                setShowPreview(true)
                            }
                            className="rounded-full border border-violet-300 bg-white px-4 py-2 text-[11px] font-bold text-violet-700 hover:bg-violet-50 disabled:opacity-50"
                        >
                            3. Preview Participant View
                        </button>

                    </div>


                    {/*--------------------------------------------------
                      Participant Preview
                    --------------------------------------------------*/}

                    {showPreview && (

                        <div className="rounded-lg border border-orange-200 bg-orange-50/50 p-3">

                            <div className="mb-3">

                                <p className="text-xs font-bold text-orange-700">
                                    Participant Sharing Preview
                                </p>

                                <p className="mt-1 text-[10px] text-gray-500">
                                    Only the information shown below
                                    will be included in this
                                    approved snapshot.
                                </p>

                                <p className="mt-2 text-[10px] text-gray-600">
                                    Evaluation Date:
                                    {' '}
                                    {formatDate(
                                        evaluationMatrix.evaluatedAt
                                    )}
                                </p>

                            </div>


                            <div className="space-y-2">

                                {previewDimensions.map(item => (

                                    <div
                                        key={item.dimensionId}
                                        className="rounded border border-orange-100 bg-white p-3"
                                    >

                                        <p className="text-[9px] text-gray-400">
                                            {item.categoryName}
                                        </p>

                                        <div className="mt-1 flex flex-wrap items-center justify-between gap-2">

                                            <p className="text-[11px] font-semibold text-gray-700">
                                                {item.dimensionName}
                                            </p>

                                            <span className="text-xs font-bold text-orange-600">
                                                {formatScore(item.score)} / 5
                                            </span>

                                        </div>

                                        {sharingMode ===
                                            'ProgressAndFeedback' &&
                                            item.sharedFeedback && (

                                            <p className="mt-2 whitespace-pre-wrap rounded bg-orange-50 p-2 text-[10px] text-gray-600">
                                                {item.sharedFeedback}
                                            </p>

                                        )}

                                    </div>

                                ))}

                            </div>


                            <div className="mt-3 border-t border-orange-100 pt-3">

                                <label className="flex items-start gap-2 text-[10px] text-gray-700">

                                    <input
                                        type="checkbox"
                                        checked={false}
                                        readOnly
                                        className="sr-only"
                                    />

                                    <span>
                                        Publishing creates a participant-visible
                                        snapshot. Later edits to the private
                                        evaluation will not automatically update it.
                                    </span>

                                </label>

                            </div>


                            {/* Publish Confirmation */}

                            <div className="mt-3 flex flex-wrap gap-2">

                                <button
                                    type="button"
                                    disabled={
                                        isBusy ||
                                        previewDimensions.length === 0
                                    }
                                    onClick={handlePublish}
                                    className="rounded-full bg-lime-600 px-4 py-2 text-[11px] font-bold text-white hover:bg-lime-700 disabled:opacity-50"
                                >
                                    {isPublishing
                                        ? 'Publishing...'
                                        : isShared
                                            ? 'Publish Revised Version'
                                            : 'Share with Participant'}
                                </button>

                                <button
                                    type="button"
                                    disabled={isBusy}
                                    onClick={() =>
                                        setShowPreview(false)
                                    }
                                    className="rounded-full border border-gray-200 bg-white px-4 py-2 text-[11px] font-semibold text-gray-600 hover:bg-gray-50"
                                >
                                    Back to Editing
                                </button>

                            </div>

                        </div>

                    )}


                    {/*--------------------------------------------------
                      Revocation
                    --------------------------------------------------*/}

                    {isShared && (

                        <div className="border-t border-rose-100 pt-3">

                            {!confirmRevoke ? (

                                <button
                                    type="button"
                                    disabled={isBusy}
                                    onClick={() =>
                                        setConfirmRevoke(true)
                                    }
                                    className="rounded-full border border-rose-300 px-4 py-2 text-[10px] font-semibold text-rose-700 hover:bg-rose-50"
                                >
                                    Revoke Participant Access
                                </button>

                            ) : (

                                <div className="rounded border border-rose-200 bg-rose-50 p-3">

                                    <p className="text-[11px] font-semibold text-rose-700">
                                        Revoke this shared evaluation?
                                    </p>

                                    <p className="mt-1 text-[10px] text-gray-600">
                                        The approved snapshot will no longer
                                        be accessible to the participant.
                                        Publication history will be retained.
                                    </p>

                                    <div className="mt-3 flex gap-2">

                                        <button
                                            type="button"
                                            disabled={isBusy}
                                            onClick={handleRevoke}
                                            className="rounded-full bg-rose-600 px-4 py-2 text-[10px] font-bold text-white disabled:opacity-50"
                                        >
                                            {isRevoking
                                                ? 'Revoking...'
                                                : 'Confirm Revoke'}
                                        </button>

                                        <button
                                            type="button"
                                            disabled={isBusy}
                                            onClick={() =>
                                                setConfirmRevoke(false)
                                            }
                                            className="rounded-full border border-gray-200 bg-white px-4 py-2 text-[10px] text-gray-600"
                                        >
                                            Cancel
                                        </button>

                                    </div>

                                </div>

                            )}

                        </div>

                    )}

                </div>

            )}


            {/*----------------------------------------------------------
              Messages
            ----------------------------------------------------------*/}

            {statusMessage && (

                <p className="mt-3 rounded bg-lime-50 p-2 text-[10px] font-semibold text-lime-700">
                    {statusMessage}
                </p>

            )}

            {errorMessage && (

                <p className="mt-3 rounded bg-rose-50 p-2 text-[10px] font-semibold text-rose-700">
                    {errorMessage}
                </p>

            )}

        </div>

    )

}


export default EtCoachUserSessionEvalSharing